import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import aguid from 'lib/aguid';
import RankingMatrixForm from './RankingMatrixForm';
import useRankingMatrixGroupByField from './useRankingMatrixGroupByField';
import { FiscalPeriodsContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider';

const DEFAULT_SORT = {
  mode: 'alphabetical',
  order: 'asc',
} as VisualisationDefinitions.RankingMatrixSorting;

const RankingMatrixFormContainer = ({
  gadget,
  onChange,
}: {
  gadget?: VisualisationDefinitions.RankingMatrix;
  onChange?: React.Dispatch<
    React.SetStateAction<undefined | VisualisationDefinition>
  >;
}) => {
  const { currentFiscalYear } = useContext(FiscalPeriodsContext);

  // Base
  const [isLoading] = useState<boolean>(false);
  const [id] = useState(gadget ? gadget.id : aguid());
  const [name, setName] = useState<string>(gadget ? gadget.name : '');
  const [description, setDescription] = useState<string>(
    gadget ? gadget.description : '',
  );

  // RankingMatrix specifics
  const [metrics, setMetrics] = useState<
    VisualisationDefinitions.RankingMatrixMetric[]
  >(gadget ? gadget.metrics : []);
  const { groupByField, groupingOptions, clearGrouping, isDateGrouping } =
    useRankingMatrixGroupByField(
      metrics,
      gadget ? gadget.groupByField : undefined,
    );
  const [interval, setInterval] = useState<FleetOps.Interval | FiscalInterval>(
    gadget && gadget.interval ? gadget.interval : 'week',
  );
  const [sorting, setSorting] =
    useState<VisualisationDefinitions.RankingMatrixSorting>(
      gadget ? gadget.sorting : DEFAULT_SORT,
    );
  const [gridSizing, setGridSizing] = useState<
    'fitGadget' | 'fitContent' | undefined
  >(gadget ? gadget.gridSizing : 'fitGadget');

  const gridSizingOptions = useMemo((): DropdownOption[] => {
    return [
      {
        label: 'Fit Gadget',
        subLabel: 'Attempt to fit all columns within the gadget space',
        isSelected: gridSizing === 'fitGadget' || gridSizing === undefined,
        onSelected: () => {
          setGridSizing('fitGadget');
        },
      },
      {
        label: 'Fit Content',
        subLabel: 'Size columns to fit their content',
        isSelected: gridSizing === 'fitContent',
        onSelected: () => {
          setGridSizing('fitContent');
        },
      },
    ];
  }, [gridSizing]);
  const [draftGadget, setDraftGadget] = useState<
    VisualisationDefinitions.RankingMatrix | undefined
  >();

  useEffect(() => {
    if (metrics.length === 0) {
      setSorting(DEFAULT_SORT);
      clearGrouping();
    }
  }, [clearGrouping, metrics.length]);

  const updateMetric = useCallback(
    (newMetric: VisualisationDefinitions.RankingMatrixMetric) => {
      setMetrics((current) =>
        current.map((c) => {
          if (c.key === newMetric.key) {
            return newMetric;
          }
          return c;
        }),
      );
    },
    [],
  );

  const onMetricAdded = useCallback((selectedMetric: Metrics.Metric) => {
    const newMetric = {
      key: aguid(),
      metricId: selectedMetric.id,
      isRankingEnabled: false,
      isPercentageOfTotalEnabled: false,
    };

    setMetrics((current) => [...current, newMetric]);
  }, []);

  const removeMetric = useCallback(
    (removedMetric: VisualisationDefinitions.RankingMatrixMetric) => {
      setMetrics((current) =>
        current.filter((m) => m.metricId !== removedMetric.metricId),
      );
    },
    [],
  );

  useEffect(() => {
    if (name !== '' && metrics.length > 0 && !!groupByField) {
      setDraftGadget({
        type: 'RankingMatrix',
        id,
        name,
        description,
        metrics,
        sorting,
        groupByField,
        // Interval only applies when grouping by a date field.
        interval: isDateGrouping ? interval : undefined,
        gridSizing,
      });
    } else {
      setDraftGadget(undefined);
    }
  }, [
    description,
    gridSizing,
    groupByField,
    id,
    interval,
    isDateGrouping,
    metrics,
    name,
    sorting,
  ]);

  const onNameChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const onDescriptionChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);
  };

  useEffect(() => {
    if (draftGadget && onChange) {
      onChange(draftGadget);
    }
  }, [draftGadget, onChange]);

  return (
    <RankingMatrixForm
      name={name}
      onNameChanged={onNameChanged}
      description={description}
      onDescriptionChanged={onDescriptionChanged}
      metrics={metrics}
      onMetricAdded={onMetricAdded}
      updateMetric={updateMetric}
      removeMetric={removeMetric}
      groupByField={groupByField}
      groupingOptions={groupingOptions}
      isDateGrouping={isDateGrouping}
      interval={interval}
      setInterval={setInterval}
      isFiscalDisabled={!currentFiscalYear}
      sorting={sorting}
      setSorting={setSorting}
      gridSizingOptions={gridSizingOptions}
      isLoading={isLoading}
      isEditing={!!gadget}
    />
  );
};

export default RankingMatrixFormContainer;
